import { useState } from 'react';
import { Anchor, BadgeDollarSign, ChartNoAxesCombined, ClipboardList, Container, Droplets, Leaf, Ship, ShipWheel } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import './Login.css';

export default function Login() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState('signin');
  const [fullName, setFullName] = useState('');
  const [organization, setOrganization] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const isRegistering = mode === 'register';

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      if (isRegistering) {
        const result = await signUp(email, password, fullName, organization);
        if (result?.pending_approval) {
          setNotice(result.message || 'Profile created. Please wait for administrator approval before signing in.');
          setMode('signin');
          setPassword('');
        }
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      setError(err.message || (isRegistering ? 'Profile creation failed.' : 'Login failed. Check your credentials.'));
    } finally {
      setLoading(false);
    }
  }

  function switchMode(nextMode) {
    setMode(nextMode);
    setError('');
    setNotice('');
  }

  return (
    <main className="login-page">
      <section className="login-visual" aria-label="Roro Fleet command overview">
        <div className="login-visual__grid" aria-hidden="true" />
        <div className="login-visual__topline">
          <div>
            <div className="login-brand">RORO <span>FLEET</span></div>
            <div className="login-kicker">VESSEL OPERATIONS MANAGEMENT SYSTEM</div>
          </div>
        </div>

        <div className="login-operations" aria-label="Fleet operations modules">
          <svg className="login-operations__lines" viewBox="0 0 760 440" aria-hidden="true">
            <defs>
              <filter id="signalGlow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path id="lineVessels" d="M150 96 L332 194" />
            <path id="linePort" d="M382 68 L380 168" />
            <path id="lineCargo" d="M610 106 L428 194" />
            <path id="lineBunkers" d="M145 332 L332 246" />
            <path id="lineEmissions" d="M382 378 L380 272" />
            <path id="lineKpis" d="M612 330 L428 246" />
            <path id="lineNoon" d="M128 220 L311 220" />
            <path id="lineCharter" d="M632 220 L449 220" />
            <circle className="login-signal login-signal--one" r="5" filter="url(#signalGlow)">
              <animateMotion dur="4.8s" repeatCount="indefinite">
                <mpath href="#lineVessels" />
              </animateMotion>
            </circle>
            <circle className="login-signal login-signal--two" r="5" filter="url(#signalGlow)">
              <animateMotion dur="5.6s" begin="1.1s" repeatCount="indefinite">
                <mpath href="#lineCargo" />
              </animateMotion>
            </circle>
            <circle className="login-signal login-signal--three" r="4" filter="url(#signalGlow)">
              <animateMotion dur="6.2s" begin=".6s" repeatCount="indefinite">
                <mpath href="#lineBunkers" />
              </animateMotion>
            </circle>
            <circle className="login-signal login-signal--four" r="4" filter="url(#signalGlow)">
              <animateMotion dur="5.2s" begin="1.8s" repeatCount="indefinite">
                <mpath href="#lineCharter" />
              </animateMotion>
            </circle>
          </svg>

          <div className="login-hub" aria-hidden="true">
            <strong>FLEET OPERATIONS</strong>
            <ShipWheel size={56} strokeWidth={1.65} />
          </div>

          {[
            ['vessels', 'Vessels', 'Fleet position', Ship],
            ['port', 'Port Calls', 'Berth flow', Anchor],
            ['cargo', 'Cargo', 'Utilization', Container],
            ['bunkers', 'Bunkers', 'Fuel cost', Droplets],
            ['emissions', 'Emissions', 'CII / ETS', Leaf],
            ['kpis', 'KPIs', 'Performance', ChartNoAxesCombined],
            ['noon', 'Noon Reports', 'Daily data', ClipboardList],
            ['charter', 'Charter P&L', 'Voyage margin', BadgeDollarSign],
          ].map(([key, label, note, Icon]) => (
            <div className={`login-module login-module--${key}`} key={key}>
              <span className="login-module__icon">
                <Icon size={30} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <span className="login-module__copy">
                <strong>{label}</strong>
                <small>{note}</small>
              </span>
            </div>
          ))}
        </div>

        <div className="login-pipeline" aria-hidden="true">
          {['Track', 'Plan', 'Check', 'Report'].map(item => (
            <div className="login-pipeline__step" key={item}>
              <span />
              {item}
            </div>
          ))}
        </div>
      </section>

      <section className="login-shell" aria-label="Account access">
        <div className="login-panel">
          <div className="login-panel__header">
            <div className="login-brand">RORO <span>FLEET</span></div>
            <div className="login-kicker">SECURE OPERATIONS ACCESS</div>
          </div>

          <div className="login-tabs" role="tablist" aria-label="Authentication mode">
            <button
              type="button"
              className={!isRegistering ? 'is-active' : ''}
              onClick={() => switchMode('signin')}
              role="tab"
              aria-selected={!isRegistering}
            >
              SIGN IN
            </button>
            <button
              type="button"
              className={isRegistering ? 'is-active' : ''}
              onClick={() => switchMode('register')}
              role="tab"
              aria-selected={isRegistering}
            >
              CREATE PROFILE
            </button>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            {isRegistering && (
              <>
                <label className="login-field">
                  <span>FULL NAME</span>
                  <input
                    type="text"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="Your name"
                    required
                  />
                </label>
                <label className="login-field">
                  <span>ORGANISATION</span>
                  <input
                    type="text"
                    value={organization}
                    onChange={e => setOrganization(e.target.value)}
                    placeholder="Company or team"
                    autoComplete="organization"
                    required
                  />
                </label>
              </>
            )}

            <label className="login-field">
              <span>EMAIL ADDRESS</span>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                autoComplete="email"
                required
              />
            </label>

            <label className="login-field">
              <span>PASSWORD</span>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete={isRegistering ? 'new-password' : 'current-password'}
                required
                minLength={isRegistering ? 8 : undefined}
              />
            </label>

            {!isRegistering && (
              <button type="button" className="login-forgot" onClick={() => setError('Contact the app administrator to reset access during this testing phase.')}>
                FORGOT PASSWORD?
              </button>
            )}

            {error && (
              <div className="login-error" role="alert">
                {error}
              </div>
            )}

            {notice && (
              <div className="login-error login-error--notice" role="status">
                {notice}
              </div>
            )}

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? (isRegistering ? 'CREATING PROFILE...' : 'SIGNING IN...') : (isRegistering ? 'CREATE MY PROFILE' : 'SIGN IN')}
            </button>
          </form>

          <div className="login-footnote">
            New profiles require administrator approval before first sign-in.
          </div>
        </div>
      </section>
    </main>
  );
}
